import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Cake, Send, Mail, MessageCircle, Users, User, Loader2, 
  RefreshCw, Sparkles, Gift, PartyPopper, Calendar, Check,
  Image as ImageIcon, Download, Copy, Upload
} from 'lucide-react';
import api from '../lib/api';

const Aniversariantes = () => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [aniversariantes, setAniversariantes] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mensagemPersonalizada, setMensagemPersonalizada] = useState('');
  const [mensagemGerada, setMensagemGerada] = useState('');
  const [imagemGerada, setImagemGerada] = useState('');
  const [schoolLogo, setSchoolLogo] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [gerandoMensagem, setGerandoMensagem] = useState(false);
  const [gerandoImagem, setGerandoImagem] = useState(false);

  useEffect(() => {
    loadAniversariantes();
    loadSchoolLogo();
  }, []);

  const loadSchoolLogo = async () => {
    try {
      const response = await api.get('/settings/school-logo');
      if (response.data?.logo_url) {
        setSchoolLogo(response.data.logo_url);
      }
    } catch (error) {
      console.log('Logo não encontrado');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/settings/school-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSchoolLogo(response.data.logo_url);
      toast.success('Logo atualizado!');
    } catch (error) {
      toast.error('Erro ao fazer upload do logo');
    }
  };

  const loadAniversariantes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/aniversariantes/hoje');
      setAniversariantes(response.data || []);
      // Selecionar todos por padrão
      setSelectedIds(new Set(response.data?.map(a => a.id) || []));
    } catch (error) {
      console.error('Erro ao carregar aniversariantes:', error);
      toast.error('Erro ao carregar aniversariantes');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    setSelectedIds(new Set(aniversariantes.map(a => a.id)));
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  const gerarMensagem = async () => {
    setGerandoMensagem(true);
    try {
      const response = await api.post('/ai/generate', {
        system_prompt: `Você é um redator criativo. Crie uma mensagem de feliz aniversário calorosa, profissional e acolhedora para enviar aos aniversariantes de uma escola. 
A mensagem deve:
- Ser curta (máximo 3 parágrafos)
- Ser adequada para envio por email e WhatsApp
- Transmitir carinho e celebração
- Ser genérica o suficiente para qualquer pessoa (aluno, professor, funcionário ou responsável)
- Incluir emojis de forma moderada
- Mencionar a escola DNA Hub de forma sutil`,
        user_prompt: 'Crie uma mensagem de feliz aniversário para os aniversariantes do dia.',
        max_tokens: 500,
      });
      
      setMensagemGerada(response.data.content);
      toast.success('Mensagem gerada com sucesso!');
    } catch (error) {
      toast.error('Erro ao gerar mensagem');
    } finally {
      setGerandoMensagem(false);
    }
  };

  const gerarImagem = async () => {
    setGerandoImagem(true);
    try {
      // Gerar imagem com texto personalizado e tema DNA (roxo e verde)
      const aniversarianteSelecionado = aniversariantes.find(a => selectedIds.has(a.id));
      const nome = aniversarianteSelecionado?.name?.split(' ')[0] || '';
      
      const response = await api.post('/ai/generate-birthday-image', {
        nome: nome,
        mensagem_curta: 'Que seu dia seja repleto de alegrias e realizações!'
      });
      
      if (response.data.image_base64) {
        // Converter base64 para data URL
        const dataUrl = `data:image/png;base64,${response.data.image_base64}`;
        setImagemGerada(dataUrl);
        toast.success('Imagem com tema DNA gerada com sucesso!');
      } else if (response.data.image_url) {
        setImagemGerada(response.data.image_url);
        toast.success('Imagem gerada com sucesso!');
      }
    } catch (error) {
      toast.error('Erro ao gerar imagem. Tente novamente.');
      console.error('Erro:', error);
    } finally {
      setGerandoImagem(false);
    }
  };

  const enviarParaTodos = async (canal) => {
    if (selectedIds.size === 0) {
      toast.error('Selecione pelo menos um aniversariante');
      return;
    }

    const mensagem = mensagemPersonalizada || mensagemGerada;
    if (!mensagem) {
      toast.error('Gere ou escreva uma mensagem primeiro');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/aniversariantes/enviar', {
        ids: Array.from(selectedIds),
        mensagem,
        imagem_url: imagemGerada,
        canal, // 'email', 'whatsapp' ou 'ambos'
      });

      toast.success(`Mensagens enviadas para ${response.data.enviados} pessoa(s)!`);
      
      if (response.data.falhas > 0) {
        toast.warning(`${response.data.falhas} envio(s) falharam`);
      }
      
      // Abrir links do WhatsApp em novas abas
      if (response.data.whatsapp_links && response.data.whatsapp_links.length > 0) {
        toast.info(`Abrindo ${response.data.whatsapp_links.length} conversa(s) no WhatsApp...`);
        
        // Abrir cada link com um delay para evitar bloqueio de popup
        response.data.whatsapp_links.forEach((link, index) => {
          setTimeout(() => {
            window.open(link, '_blank');
          }, index * 800);
        });
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagens');
    } finally {
      setSending(false);
    }
  };

  const getCategoriaBadge = (tipo) => {
    const cores = {
      aluno: 'bg-blue-100 text-blue-800',
      responsavel: 'bg-green-100 text-green-800',
      funcionario: 'bg-purple-100 text-purple-800',
      professor: 'bg-orange-100 text-orange-800',
    };
    return cores[tipo] || 'bg-gray-100 text-gray-800';
  };

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return null;
    const match = String(dataNascimento).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    const [, y, m, d] = match;
    const hoje = new Date();
    let idade = hoje.getFullYear() - parseInt(y);
    const mDiff = (hoje.getMonth() + 1) - parseInt(m);
    if (mDiff < 0 || (mDiff === 0 && hoje.getDate() < parseInt(d))) idade--;
    return idade;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cake className="w-7 h-7 text-pink-500" />
            Aniversariantes do Dia
          </h1>
          <p className="text-muted-foreground mt-1">
            Envie felicitações para todos os aniversariantes de hoje
          </p>
        </div>
        <Button variant="outline" onClick={loadAniversariantes}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lista de Aniversariantes */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <PartyPopper className="w-5 h-5 text-yellow-500" />
                  {aniversariantes.length} Aniversariante(s) Hoje
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll}>
                    Selecionar Todos
                  </Button>
                  <Button variant="outline" size="sm" onClick={deselectAll}>
                    Limpar
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {aniversariantes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Cake className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nenhum aniversariante hoje</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {aniversariantes.map((pessoa) => (
                    <div
                      key={pessoa.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedIds.has(pessoa.id) 
                          ? 'bg-pink-50 border-pink-300' 
                          : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleSelect(pessoa.id)}
                    >
                      <Checkbox
                        checked={selectedIds.has(pessoa.id)}
                        className="pointer-events-none"
                      />
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center text-white font-bold">
                        {pessoa.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{pessoa.name}</p>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          {pessoa.email && <span>{pessoa.email}</span>}
                          {pessoa.phone && <span>• {pessoa.phone}</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getCategoriaBadge(pessoa.tipo)}>
                          {pessoa.tipo || 'Pessoa'}
                        </Badge>
                        {pessoa.birth_date && (
                          <p className="text-sm text-muted-foreground mt-1">
                            {calcularIdade(pessoa.birth_date)} anos
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Painel de Ações */}
        <div className="space-y-4">
          {/* Gerar Mensagem */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                Mensagem
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={gerarMensagem} 
                disabled={gerandoMensagem}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {gerandoMensagem ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando...</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-2" /> Gerar com IA</>
                )}
              </Button>
              
              {mensagemGerada && (
                <div className="p-3 bg-purple-50 rounded-lg text-sm">
                  {mensagemGerada}
                </div>
              )}

              <div className="text-xs text-muted-foreground text-center">ou escreva sua própria:</div>
              
              <Textarea
                placeholder="Digite sua mensagem personalizada..."
                value={mensagemPersonalizada}
                onChange={(e) => setMensagemPersonalizada(e.target.value)}
                className="min-h-[100px]"
              />
            </CardContent>
          </Card>

          {/* Gerar Imagem */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-pink-500" />
                Imagem (Opcional)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Logo da Escola */}
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 border rounded flex items-center justify-center overflow-hidden bg-white">
                  {schoolLogo ? (
                    <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <Upload className="w-5 h-5 text-gray-400" />
                  )}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-medium">Logo da Escola</p>
                  <p className="text-xs text-muted-foreground">{schoolLogo ? 'Configurado' : 'Não configurado'}</p>
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} />
                  <Button variant="outline" size="sm" asChild>
                    <span>{schoolLogo ? 'Trocar' : 'Upload'}</span>
                  </Button>
                </label>
              </div>

              <Button 
                onClick={gerarImagem} 
                disabled={gerandoImagem}
                variant="outline"
                className="w-full"
              >
                {gerandoImagem ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Gerando...</>
                ) : (
                  <><ImageIcon className="w-4 h-4 mr-2" /> Gerar Imagem com IA (Tema DNA)</>
                )}
              </Button>
              
              {imagemGerada && (
                <img 
                  src={imagemGerada} 
                  alt="Feliz Aniversário" 
                  className="w-full rounded-lg border"
                />
              )}
            </CardContent>
          </Card>

          {/* Botões de Envio */}
          <Card className="border-2 border-pink-200">
            <CardHeader className="pb-3 bg-gradient-to-r from-pink-50 to-purple-50">
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="w-5 h-5 text-pink-600" />
                Enviar Felicitações
              </CardTitle>
              <CardDescription>
                {selectedIds.size} pessoa(s) selecionada(s)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              <Button
                onClick={() => enviarParaTodos('email')}
                disabled={sending || selectedIds.size === 0}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4 mr-2" />
                )}
                Enviar por E-mail
              </Button>
              
              <Button
                onClick={() => enviarParaTodos('whatsapp')}
                disabled={sending || selectedIds.size === 0}
                className="w-full bg-green-600 hover:bg-green-700"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-2" />
                )}
                Enviar por WhatsApp
              </Button>
              
              <Button
                onClick={() => enviarParaTodos('ambos')}
                disabled={sending || selectedIds.size === 0}
                className="w-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Gift className="w-4 h-4 mr-2" />
                )}
                Enviar E-mail + WhatsApp
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Aniversariantes;
